/**
 * Klaviyo-Integration (Admin) — dünner Client für die Edge Function `admin-klaviyo`.
 * Der API-Key wird nur gesendet, nie zurückgelesen (server-only Secret).
 */

import { supabase } from '@/lib/supabase';

export interface KlaviyoStatus {
  configured: boolean;
  enabled: boolean;
  listId: string | null;
  lastSyncAt: string | null;
  lastSyncCount: number | null;
  lastSyncError: string | null;
}

type Action =
  | { action: 'status' }
  | { action: 'save'; apiKey?: string; listId: string }
  | { action: 'sync' }
  | { action: 'seed' }
  | { action: 'consent-backfill' };

async function invoke<T>(payload: Action): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase.functions.invoke('admin-klaviyo', {
    body: payload,
    headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });
  if (error) throw new Error(error.message);
  if (data && data.success === false) throw new Error(data.error || 'Klaviyo-Anfrage fehlgeschlagen');
  return data as T;
}

export const fetchKlaviyoStatus = (): Promise<KlaviyoStatus> =>
  invoke<KlaviyoStatus>({ action: 'status' });

/** apiKey leer lassen, um den gespeicherten Key zu behalten (nur List-ID ändern). */
export const saveKlaviyoConfig = (listId: string, apiKey?: string): Promise<KlaviyoStatus> =>
  invoke<KlaviyoStatus>({ action: 'save', listId, apiKey });

export const syncKlaviyoUsers = (): Promise<{ success: boolean; syncedCount: number }> =>
  invoke<{ success: boolean; syncedCount: number }>({ action: 'sync' });

/** Schema-Seed: synthetisches Profil + je Metric ein Event, damit alle Properties/Metrics in Klaviyo registrieren. */
export const seedKlaviyoSchema = (): Promise<{ success: boolean; seededMetrics: number }> =>
  invoke<{ success: boolean; seededMetrics: number }>({ action: 'seed' });

/**
 * Consent-Backfill: setzt marketing_consent für alle bestehenden Nutzer auf true
 * und meldet sie in Klaviyo als SUBSCRIBED (Consent wird extern verwaltet).
 * `dbUpdatedCount` = Anzahl neu gesetzter Profile, `subscribedCount` = an Klaviyo gemeldet.
 */
export const backfillKlaviyoConsent = (): Promise<{ success: boolean; dbUpdatedCount: number; subscribedCount: number }> =>
  invoke<{ success: boolean; dbUpdatedCount: number; subscribedCount: number }>({ action: 'consent-backfill' });
